import { Contractor } from "@components/modules/contractors/ContractorModel";
import type { IOption } from "@components/ui-kit";
import { t } from "@lingui/macro";
import {
  type AutocompleteCompanyData,
  BusinessFormEnum,
  type CompanyBankAccount,
} from "@src/__generated__/urql-graphql";
import { appStore } from "@src/stores/AppStore";
import { required } from "@src/utils/forms/validators";
import { FieldState, FormState } from "formstate";
import { action, computed, makeObservable, observable } from "mobx";
import type { DeepNonNullable, MarkRequired } from "ts-essentials";
import type {
  AccountingAccountSimpleMap,
  CountrySimpleMap,
} from "../graphql/Queries.types";

type autocompleteOptions = {
  name: string;
  city: string;
  reg_no: string;
};

type BankAccountWithIban = MarkRequired<
  DeepNonNullable<CompanyBankAccount>,
  "iban"
>;
type BankAccountWithSwift = MarkRequired<
  DeepNonNullable<CompanyBankAccount>,
  "swift"
>;

export class Form {
  @observable.ref countryOptions: IOption[] = [];
  @observable.ref countryList: CountrySimpleMap = [];
  @observable.ref accountingAccountOptions: IOption[] = [];
  @observable.ref autoCompleteNameOptions: autocompleteOptions[] = [];
  @observable.ref autoCompleteRegistrationNoOptions: autocompleteOptions[] = [];
  @observable.ref bankAccounts: CompanyBankAccount[] = [];

  @computed
  get ibanOptions(): IOption[] {
    return (this.bankAccounts || [])
      .filter<BankAccountWithIban>(
        (account): account is BankAccountWithIban =>
          typeof account.iban === "string",
      )
      .map((account) => ({
        label: account.iban,
        value: account.iban,
      }));
  }

  @computed
  get swiftOptions(): IOption[] {
    return (this.bankAccounts || [])
      .filter<BankAccountWithSwift>(
        (account): account is BankAccountWithSwift =>
          typeof account.swift === "string",
      )
      .map((account) => ({
        label: account.swift,
        value: account.swift,
      }));
  }

  @computed
  get usedIbanFromAutocompletedBankAccounts(): boolean {
    return !!this.bankAccounts.find(
      (account) => account.iban === this.iban.value,
    )?.iban;
  }

  businessFormOptions = [
    {
      label: t`Natural person`,
      value: BusinessFormEnum.Person,
    },
    {
      label: t`Company`,
      value: BusinessFormEnum.Company,
    },
  ];

  constructor() {
    makeObservable(this);
  }

  private regNoValidator = (value: string) => {
    if (!this.extraValidationEnabled) return null;
    return this.business_form.value === BusinessFormEnum.Company
      ? required(value)
      : null;
  };

  private requiredIfVatPayer = (value: string) => {
    if (!this.extraValidationEnabled) return null;
    return this.vat_payer.value ? required(value) : null;
  };

  legal_name = new FieldState("")
    .setAutoValidationDefault(false)
    .validators(required);
  street = new FieldState("")
    .setAutoValidationDefault(false)
    .validators(required);
  city = new FieldState("")
    .setAutoValidationDefault(false)
    .validators(required);
  zip = new FieldState("").setAutoValidationDefault(false).validators(required);
  tax_no = new FieldState("");
  vat_no = new FieldState("").validators(this.requiredIfVatPayer);
  vat_payer = new FieldState(false);
  @observable
  country_id = new FieldState("")
    .setAutoValidationDefault(false)
    .validators(required);
  national_id = new FieldState("");
  registration_no = new FieldState("").validators(this.regNoValidator);
  business_form = new FieldState<BusinessFormEnum>(BusinessFormEnum.Person)
    .setAutoValidationDefault(false)
    .validators(required);
  default_accounting_account_id = new FieldState("");
  iban = new FieldState("");
  swift = new FieldState("");
  bank_account = new FieldState("");
  bank_account_code = new FieldState("");

  readonly _allFieldsProxy = new FormState([
    this.legal_name,
    this.street,
    this.city,
    this.zip,
    this.tax_no,
    this.vat_no,
    this.vat_payer,
    this.country_id,
    this.business_form,
    this.iban,
    this.swift,
    this.registration_no,
    this.national_id,
    this.bank_account,
    this.bank_account_code,
  ]);

  public fill(src: {
    legal_name?: string | null;
    street?: string | null;
    city?: string | null;
    zip?: string | null;
    registration_no?: string | null;
    tax_no?: string | null;
    vat_no?: string | null;
    vat_payer?: boolean | null;
    national_id?: string | null;
    iban?: string | null;
    swift?: string | null;
    business_form?: BusinessFormEnum | null;
    country_id?: string | null;
    default_accounting_account_id?: string | null;
    bank_account?: string | null;
    bank_account_code?: string | null;
  }): void {
    this.legal_name.onChange(src.legal_name ?? "");
    this.street.onChange(src.street ?? "");
    this.city.onChange(src.city ?? "");
    this.zip.onChange(src.zip ?? "");
    this.registration_no.onChange(src.registration_no ?? "");
    this.tax_no.onChange(src.tax_no ?? "");
    this.vat_no.onChange(src.vat_no ?? "");
    this.vat_payer.onChange(!!src.vat_no);
    this.national_id.onChange(src.national_id ?? "");
    if (src.iban) {
      this.iban.onChange(src.iban);
    }
    this.swift.onChange(src.swift ?? "");
    this.business_form.onChange(
      src.business_form ??
        (src.tax_no ? BusinessFormEnum.Company : BusinessFormEnum.Person),
    );
    this.country_id.onChange(src.country_id ?? "");
    this.default_accounting_account_id.onChange(
      src.default_accounting_account_id ?? "",
    );
    this.bank_account.onChange(src.bank_account ?? "");
    this.bank_account_code.onChange(src.bank_account_code ?? "");
  }

  @action.bound
  setCountryOptions(src: CountrySimpleMap) {
    this.countryOptions = src.map((i) => ({ label: i.name, value: i.id }));
    this.setCountryList(src);
  }

  @action.bound
  setCountryList(src: CountrySimpleMap) {
    this.countryList = src;
  }

  @action.bound
  setAutoCompleteNameOptions(src: AutocompleteCompanyData["results"]) {
    this.autoCompleteNameOptions = src.map((i) => ({
      name: i.legal_name,
      city: i.city,
      reg_no: i.national_id,
    })) as autocompleteOptions[];
  }

  @action.bound
  setAutoCompleteRegistrationNoOptions(
    src: AutocompleteCompanyData["results"],
  ) {
    this.autoCompleteRegistrationNoOptions = src.map((i) => ({
      name: i.legal_name,
      city: i.city,
      reg_no: i.national_id,
    })) as autocompleteOptions[];
  }

  @action.bound
  setAccountingAccountOptions(src: AccountingAccountSimpleMap) {
    this.accountingAccountOptions = src.map((i) => ({
      label: `${i.account_number} - ${i.title}`,
      value: i.id,
    }));
  }

  @action.bound
  handleTaxNoChange(value: string): void {
    this.tax_no.onChange(value);
    this.updateVatPayerFromTaxFields();
  }

  @action.bound
  handleVatNoChange(value: string): void {
    this.vat_no.onChange(value);
    this.updateVatPayerFromTaxFields();
  }

  @action.bound
  handleVatPayerChange(value: boolean): void {
    this.vat_payer.onChange(value);
    this.vat_no.validate();
  }

  @action.bound
  private updateVatPayerFromTaxFields(): void {
    if (!this.extraValidationEnabled) return;

    const hasTaxNo = this.tax_no.value.trim() !== "";
    const hasVatNo = this.vat_no.value.trim() !== "";

    const newValue = hasTaxNo || hasVatNo;
    this.handleVatPayerChange(newValue);
  }

  public reset() {
    this._allFieldsProxy.reset();
  }

  @computed
  get extraValidationEnabled(): boolean {
    return (
      appStore.workspaceStore.settings?.contractor_fields_extra_validation ??
      false
    );
  }

  public async validateForContractor() {
    const fieldsToValidate: Array<
      FieldState<string> | FieldState<BusinessFormEnum>
    > = [this.legal_name, this.street, this.city, this.zip];

    if (this.extraValidationEnabled) {
      fieldsToValidate.push(
        this.registration_no,
        this.country_id,
        this.business_form,
        this.vat_no,
      );
    }

    return await new FormState(fieldsToValidate).validate();
  }

  public async validateForExpense(optionalFields?: {
    swift?: boolean;
    iban?: boolean;
  }) {
    const fields: FieldState<any>[] = [
      this.legal_name,
      this.street,
      this.city,
      this.zip,
      this.country_id,
      this.business_form,
    ];

    if (
      this.business_form.value === BusinessFormEnum.Person &&
      !this.registration_no.value
    ) {
      fields.push(this.national_id);
    } else {
      fields.push(this.registration_no);
    }

    if (optionalFields?.swift) {
      fields.push(this.swift);
    }

    if (optionalFields?.iban) {
      fields.push(this.iban);
    }

    if (this.extraValidationEnabled) {
      fields.push(this.vat_no);
    }

    return await new FormState(fields).validate();
  }

  public serialize() {
    return {
      legal_name: this.legal_name.value,
      street: this.street.value,
      city: this.city.value,
      zip: this.zip.value,
      tax_no: this.tax_no.value,
      vat_no: this.vat_payer.value === true ? this.vat_no.value : "",
      iban: this.iban.value.replace(" ", ""),
      swift: this.swift.value,
      country_id: this.country_id.value,
      business_form: this.business_form.value,
      default_accounting_account_id:
        this.default_accounting_account_id.value || undefined,
      national_id: this.national_id.value || undefined,
      registration_no: this.registration_no.value || undefined,
      bank_account:
        this.bank_account.$ !== ""
          ? this.bank_account.$.replaceAll(" ", "")
          : null,
      bank_account_code:
        this.bank_account_code.$ !== ""
          ? this.bank_account_code.$.replaceAll(" ", "")
          : null,
    };
  }

  public toModel(id: string) {
    const { country_id, default_accounting_account_id, ...data } =
      this.serialize();
    const country = this.countryOptions.find((i) => i.value === country_id);
    return new Contractor({
      id,
      country: country
        ? { id: country.value, name: country.label, code: "" }
        : undefined,
      defaultAccountingAccount: default_accounting_account_id
        ? { id: default_accounting_account_id }
        : undefined,
      ...data,
    });
  }
}
